<template>
  <b-container fluid id="icons-and-logos" class="contents">
    <b-row>
      <b-col>
        <span class="contents-title">Icons &amp; Logos</span>
        <i class="guide-tooltip" id="contents-tooltip"></i>
        <b-popover
          target="contents-tooltip"
          triggers="hover"
          content="Update your online store branding with the right assets to make it easily recognizable."
        ></b-popover>
      </b-col>
    </b-row>
    <div
      v-if="loading"
      class="w-100 h-100 d-flex align-items-center justify-content-center"
    >
      Loading...
    </div>
    <template v-else>
      <b-row class="mt-3">
        <b-col class="position-relative" lg="3" v-if="!isGlifeOnly()">
          <label for="upload-web-favicon" class="primary-label">Upload Web Favicon</label>
          <img
            v-if="webFavicon"
            src="@/assets/images/icons/remove.svg"
            alt="remove-favicon"
            class="remove-image"
            @click="setWebFavicon('')"
          />
          <div
            class="
              image-placeholder
              mt-2
              cursor-pointer
              d-flex
              justify-content-center
              align-items-center
            "
            :class="{ 'bg-none': webFavicon }"
            @click="$root.$emit('bv::show::modal', 'upload-web-favicon-modal')"
          >
            <div v-if="webFavicon">
              <b-img :src="webFavicon" style="height: 32px; width: 32px" />
            </div>
            <span class="camera-icon-container bg-white mr-2 mb-1">
              <img src="@/assets/images/camera.svg" alt="favicon-camera-icon" />
            </span>
          </div>
          <em class="image-size">Required Size - 32 x 32 px</em>
        </b-col>
        <b-col class="position-relative" lg="3">
          <label for="upload-app-icon" class="primary-label">Upload App Icon</label>
          <img
            v-if="appIcon"
            src="@/assets/images/icons/remove.svg"
            alt="remove-app-icon"
            class="remove-image"
            @click="setAppIcon('')"
          />
          <div
            class="image-placeholder mt-2 cursor-pointer"
            @click="$root.$emit('bv::show::modal', 'upload-app-icon-modal')"
          >
            <div v-if="appIcon">
              <b-img :src="appIcon" fluid />
            </div>
            <span class="camera-icon-container bg-white mr-2 mb-1">
              <img src="@/assets/images/camera.svg" alt="camera-app-icon" />
            </span>
          </div>
          <em class="image-size">Required Size - 125 x 125 px</em>
        </b-col>
        <b-col class="position-relative" lg="4" v-if="!isGlifeOnly()">
          <label for="upload-web-logo" class="primary-label">Upload Web Logo</label>
          <img
            v-if="webLogo"
            src="@/assets/images/icons/remove.svg"
            alt="remove-web-logo-icon"
            class="remove-image"
            style="left: 325px"
            @click="setWebLogo('')"
          />
          <div
            class="image-placeholder mt-2 cursor-pointer"
            @click="$root.$emit('bv::show::modal', 'upload-web-logo-modal')"
            style="width: 304px; height: 127px"
          >
            <div v-if="webLogo">
              <b-img :src="webLogo" fluid />
            </div>
            <span class="camera-icon-container bg-white mr-2 mb-1">
              <img src="@/assets/images/camera.svg" alt="web-logo-camera-icon" />
            </span>
          </div>
          <em class="image-size">Required Size - 304 x 127 px</em>
        </b-col>
      </b-row>
    </template>
    <!-- MODALS START -->
    <UploadWebFaviconModal />
    <UploadAppIconModal />
    <UploadWebLogoModal />
    <!-- MODALS END -->
  </b-container>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import UploadWebFaviconModal from "../modals/UploadWebFaviconModal";
import UploadAppIconModal from "../modals/UploadAppIconModal";
import UploadWebLogoModal from "../modals/UploadWebLogoModal";
import { subscriptionChannels } from "@/mixins/subscription_channels";

export default {
  data() {
    return {
      isDataLoaded: false,
    };
  },
  computed: {
    ...mapState("designs", [
      "webFavicon",
      "appIcon",
      "webLogo",
      "iconsAndLogos",
      "loading",
      "errors",
    ]),
    ...mapGetters("accountProfile", ["planChannels"])
  },
  mixins: [subscriptionChannels],
  methods: {
    ...mapMutations("designs", ["setWebFavicon", "setAppIcon", "setWebLogo"]),
    ...mapActions("designs", ["saveIconsAndLogos", "getIconsAndLogos"]),
    async save() {
      await this.saveIconsAndLogos({
        icon_enable: true,
        favicon: this.webFavicon,
        app_icon: this.appIcon,
        logo: this.webLogo,
      });

      if (!this.errors) this.$root.$emit("showSaveAlert", false);
    },
  },
  components: {
    UploadWebFaviconModal,
    UploadAppIconModal,
    UploadWebLogoModal,
  },
  watch: {
    webFavicon() {
      if (this.isDataLoaded) this.$root.$emit("showSaveAlert", true);
    },
    appIcon() {
      if (this.isDataLoaded) this.$root.$emit("showSaveAlert", true);
    },
    webLogo() {
      if (this.isDataLoaded) this.$root.$emit("showSaveAlert", true);
    },
  },
  mounted() {
    this.$root.$on("handleSave", (routeName) => {
      if (routeName === "designAdvancedSettingsIconsAndLogos") this.save();
    });
    this.$root.$on("saveChangesCancelled", (isCancelled) => {
      if (isCancelled) {
        this.isDataLoaded = false;
        const { favicon, app_icon, logo } = this.iconsAndLogos;

        this.setWebFavicon(favicon == null ? '' : favicon);
        this.setAppIcon(app_icon);
        this.setWebLogo(logo);

        this.$nextTick(() => {
          this.isDataLoaded = true;
        });
      }
    });
  },
  async created() {
    await this.getIconsAndLogos();

    const { favicon, app_icon, logo } = this.iconsAndLogos;

    this.setWebFavicon(favicon == null ? '' : favicon);
    this.setAppIcon(app_icon);
    this.setWebLogo(logo);
    this.$nextTick(() => {
      this.isDataLoaded = true;
    });
  },
  beforeDestroy() {
    this.$root.$off("handleSave");
    this.$root.$off("saveChangesCancelled");
  },
};
</script>
